import { formatCurrency, formatInteger, formatPercent, formatRatio } from '@/design'
import type { AdvertisingMetrics, AdvertisingReport, TmtAttributedMetrics } from '@/api/types'
import { dayCount, formatWindow } from '@/lib/filters'

/** The six TmT columns' own keys — camelCase, `tmt`-prefixed so `wireSortField`
 * turns a click on one into the API's `tmt_purchases` etc., never colliding with a
 * provider sort key. */
export type TmtColumnKey =
  | 'tmtPurchases'
  | 'tmtRevenueMinor'
  | 'tmtNewCustomers'
  | 'tmtCpaMinor'
  | 'tmtRoas'
  | 'tmtCostPerNewCustomerMinor'

/** A TmT column's own key to the field it reads off `AdvertisingRow.tmt` — never
 * `model`/`tmtAttributed`, which every row's `tmt` block carries but no column reads. */
type TmtMetricField = Exclude<keyof TmtAttributedMetrics, 'model' | 'tmtAttributed'>

export const TMT_METRIC_KEY: Record<TmtColumnKey, TmtMetricField> = {
  tmtPurchases: 'purchases',
  tmtRevenueMinor: 'revenueMinor',
  tmtNewCustomers: 'newCustomers',
  tmtCpaMinor: 'cpaMinor',
  tmtRoas: 'roas',
  tmtCostPerNewCustomerMinor: 'costPerNewCustomerMinor',
}

interface ColumnDefBase {
  header: string
  /** True on the first column of an attributed block, so the table can mark where
   * a new block starts (a vertical rule, and the block's own badge). */
  groupStart?: boolean
}

/** A plain delivery column (Spend, Impressions, …) or one of the three
 * provider-attributed ones — both read off `row.metrics`/`row.previous`.
 * `tmtAttributed` is narrowed to `false | undefined` (never `true`) so a plain
 * `if (column.tmtAttributed)` check discriminates the union below without a cast. */
interface ProviderColumnDef extends ColumnDefBase {
  key: keyof AdvertisingMetrics
  /** True for the three provider-attributed columns (purchases, revenue, ROAS). */
  providerAttributed?: boolean
  tmtAttributed?: false
}

/** One of the six TmT-attributed columns — read off `row.tmt`/`row.tmtPrevious`,
 * never `row.metrics`/`row.previous`: never the same column as a provider one. */
interface TmtColumnDef extends ColumnDefBase {
  key: TmtColumnKey
  tmtAttributed: true
  providerAttributed?: false
}

export type ColumnDef = ProviderColumnDef | TmtColumnDef

/** The columns, in the order the table and the CSV show them: Meta's own delivery
 * and attribution first, then TmT's own attributed figures — two visibly distinct
 * blocks (06 §6.10), never mixed into one column list. */
export const COLUMNS: readonly ColumnDef[] = [
  { key: 'spendMinor', header: 'Spend' },
  { key: 'impressions', header: 'Impressions' },
  { key: 'clicks', header: 'Clicks' },
  { key: 'ctr', header: 'CTR' },
  { key: 'cpcMinor', header: 'CPC' },
  { key: 'cpmMinor', header: 'CPM' },
  { key: 'providerPurchases', header: 'Purchases', providerAttributed: true, groupStart: true },
  { key: 'providerPurchaseRevenueMinor', header: 'Revenue', providerAttributed: true },
  { key: 'providerRoas', header: 'ROAS', providerAttributed: true },
  { key: 'tmtPurchases', header: 'Purchases', tmtAttributed: true, groupStart: true },
  { key: 'tmtRevenueMinor', header: 'Revenue', tmtAttributed: true },
  { key: 'tmtNewCustomers', header: 'New customers', tmtAttributed: true },
  { key: 'tmtCpaMinor', header: 'CPA', tmtAttributed: true },
  { key: 'tmtRoas', header: 'ROAS', tmtAttributed: true },
  { key: 'tmtCostPerNewCustomerMinor', header: 'Cost per new customer', tmtAttributed: true },
]

/**
 * ISO 4217 currencies whose minor unit is not a hundredth — the same fixed
 * table the API's own `money.py` owns (`_ZERO_DECIMAL` / `_THREE_DECIMAL`),
 * mirrored here rather than asked of `Intl.NumberFormat`: the resolved
 * `maximumFractionDigits` can differ from ISO 4217 for a currency ICU treats
 * specially, and — for a currency code the runtime's ICU data does not
 * recognise at all — throws a `RangeError` instead of answering, which would
 * crash the cell it renders in. A fixed, never-throwing table is what the
 * backend was written to guarantee; this keeps the frontend's answer unable
 * to drift from it.
 */
const ZERO_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  'BIF', 'CLP', 'DJF', 'GNF', 'ISK', 'JPY', 'KMF', 'KRW', 'PYG', 'RWF', 'UGX', 'UYI', 'VND',
  'VUV', 'XAF', 'XOF', 'XPF',
])  // fmt: skip
const THREE_DECIMAL_CURRENCIES: ReadonlySet<string> = new Set([
  'BHD', 'IQD', 'JOD', 'KWD', 'LYD', 'OMR', 'TND',
])  // fmt: skip

function minorUnitExponent(currency: string): number {
  const code = currency.toUpperCase()
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3
  return 2
}

/** Minor units to major, in the report's own currency: the one place money becomes a display figure. */
export function formatMoney(minor: number | null, currency: string): string {
  if (minor === null) return '—'
  return formatCurrency(minor / 10 ** minorUnitExponent(currency), { currency })
}

export function formatMetric(key: ColumnDef['key'], value: number | null, currency: string): string {
  if (value === null) return '—'
  switch (key) {
    case 'spendMinor':
    case 'cpcMinor':
    case 'cpmMinor':
    case 'providerCpaMinor':
    case 'providerPurchaseRevenueMinor':
    case 'tmtRevenueMinor':
    case 'tmtCpaMinor':
    case 'tmtCostPerNewCustomerMinor':
      return formatMoney(value, currency)
    case 'ctr':
      return formatPercent(value)
    case 'providerRoas':
    case 'tmtRoas':
      return formatRatio(value)
    default:
      return formatInteger(value)
  }
}

export function windowLabel(from: string, to: string): string {
  return formatWindow({ from, to, days: dayCount(from, to) })
}

export function subtitleFor(report: AdvertisingReport): string {
  const current = windowLabel(report.start, report.end)
  if (!report.compared || !report.previousStart || !report.previousEnd) return current
  return `${current} · compared with ${windowLabel(report.previousStart, report.previousEnd)}`
}

/** The attribution windows as one sentence for a badge tooltip, e.g. "7d_click, 1d_view". */
export function attributionWindowsLabel(windows: readonly string[]): string {
  return windows.length > 0 ? windows.join(', ') : 'unknown'
}
